import { createContext, useContext, useState, type ReactNode } from 'react'

type UploadState = {
  isUploading: boolean
  error: string | null
}

type FileUploadContextType = {
  uploadStates: Record<string, UploadState>
  setUploadState: (uploadId: string, state: UploadState) => void
  getUploadState: (uploadId: string) => UploadState
  clearError: (uploadId: string) => void
}

const FileUploadContext = createContext<FileUploadContextType | null>(null)

export const FileUploadProvider = ({ children }: { children: ReactNode }) => {
  const [uploadStates, setUploadStates] = useState<Record<string, UploadState>>(
    {}
  )

  const setUploadState = (uploadId: string, state: UploadState) => {
    setUploadStates((prev) => ({
      ...prev,
      [uploadId]: state,
    }))
  }

  const getUploadState = (uploadId: string) => {
    return uploadStates[uploadId] || { isUploading: false, error: null }
  }

  const clearError = (uploadId: string) => {
    setUploadState(uploadId, { isUploading: false, error: null })
  }

  return (
    <FileUploadContext.Provider
      value={{
        uploadStates,
        setUploadState,
        getUploadState,
        clearError,
      }}
    >
      {children}
    </FileUploadContext.Provider>
  )
}

export const useFileUploadContext = () => {
  const context = useContext(FileUploadContext)
  if (!context) {
    throw new Error(
      'useFileUploadContext must be used within FileUploadProvider'
    )
  }
  return context
}
